import { disciplineForSection, type GymGradeConfig } from '@structural-climbing/validation'
import type { SessionTop } from './useMySession'

/**
 * The points one top earns on the gym's board, by the same rule as the
 * standings (`get_gym_standings`): a boulder scores its grade's base points
 * from the gym's grade settings (grade × 100 when unset), a lead route
 * (grade + 1) × 10 on the French scale, and a flash adds 10%.
 */
export function topPoints(top: SessionTop, config: GymGradeConfig): number {
  if (top.grade === null) return 0
  const base =
    disciplineForSection(top.discipline) === 'lead'
      ? (top.grade + 1) * 10
      : (config.boulder.grades[top.grade - 1]?.baseScore ?? top.grade * 100)
  return base + (top.flash ? Math.round(base / 10) : 0)
}

/**
 * Today's points, added up from today's tops.
 *
 * It used to be the change in your board score since the first time Home saw
 * you during the visit. A new member is not on the board before their first
 * top, so that first look already included it and the session said 0 points.
 * Counting the tops themselves has no such blind spot and needs no request.
 */
export function pointsToday(tops: SessionTop[] | null, config: GymGradeConfig): number | null {
  if (!tops) return null
  // The board counts a route once, ever: a repeat of an old top earns nothing,
  // and logging the same route twice today counts it once.
  const counted = new Set<string>()
  let sum = 0
  for (const top of tops) {
    if (top.firstTop === false || counted.has(top.routeId)) continue
    counted.add(top.routeId)
    sum += topPoints(top, config)
  }
  return sum
}
